<template>
  <div class="container">
    <div class="container01">
      <h1 class="container01_h1">订单管理</h1>

      <!-- 筛选条件 -->
      <el-card header="筛选条件" style="margin-bottom: 20px;">
        <el-form :model="filterForm" label-width="100px">
          <el-row :gutter="20">
            <el-col :span="6">
              <el-form-item label="客户">
                <el-select v-model="filterForm.customer_id" clearable placeholder="选择客户" filterable>
                  <el-option v-for="customer in customers" :key="customer.customer_id" :label="customer.name"
                    :value="customer.customer_id">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="状态">
                <el-select v-model="filterForm.status" clearable placeholder="选择状态">
                  <el-option label="正常" value="正常"></el-option>
                  <el-option label="已完成" value="已完成"></el-option>
                  <el-option label="已取消" value="已取消"></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="开始日期">
                <el-date-picker v-model="filterForm.start_date" type="date" placeholder="选择开始日期"
                  value-format="YYYY-MM-DD">
                </el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="6">
              <el-form-item label="结束日期">
                <el-date-picker v-model="filterForm.end_date" type="date" placeholder="选择结束日期"
                  value-format="YYYY-MM-DD">
                </el-date-picker>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item>
            <el-button type="primary" @click="handleFilter">筛选</el-button>
            <el-button @click="resetFilter">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 操作按钮 -->
      <el-row :gutter="20">
        <el-col :span="24" class="button-col">
          <div class="container01_button">
            <el-button @click="$router.back()">返回上一级</el-button>
            <el-button type="primary" @click="$router.push('/orders/create')">新建订单</el-button>
            <!-- 添加批量打印按钮 -->
            <el-button type="success" @click="handleBatchPrint" :disabled="selectedOrderIds.length === 0"
              :loading="batchPrinting">
              批量打印选中订单 ({{ selectedOrderIds.length }})
            </el-button>
            <el-button type="danger" @click="handleBatchDelete" :disabled="selectedOrderIds.length === 0"
              :loading="batchDeleting">
              删除选中订单 ({{ selectedOrderIds.length }})
            </el-button>
          </div>
        </el-col>
      </el-row>

      <!-- 搜索栏 -->
      <el-row :gutter="20">
        <el-col :span="24" class="search-col">
          <el-input v-model="searchKeyword" placeholder="请输入订单编号或客户名称" clearable class="container01_search"
            @keyup.enter="handleSearch"></el-input>
          <el-button type="primary" @click="handleSearch" style="margin-top: 20px;">搜索</el-button>
        </el-col>
      </el-row>
    </div>

    <!-- 订单表格 -->
    <div class="container02">
      <el-table :data="paginatedData" border style="width: 100%" v-loading="loading"
        @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="order_number" label="订单编号" width="150"></el-table-column>
        <el-table-column prop="customer_name" label="客户名称" min-width="150"></el-table-column>
        <el-table-column prop="order_time" label="开单时间" width="180">
          <template #default="scope">
            {{ formatDateTime(scope.row.order_time) }}
          </template>
        </el-table-column>
        <el-table-column prop="delivery_time" label="配送时间" width="180">
          <template #default="scope">
            {{ formatDateTime(scope.row.delivery_time) }}
          </template>
        </el-table-column>
        <el-table-column prop="total_amount" label="订单金额" width="120">
          <template #default="scope">
            ¥{{ scope.row.total_amount?.toFixed(2) || '0.00' }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.status)">{{ scope.row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250">
          <template #default="scope">
            <el-button type="text" @click="handleViewDetail(scope.row.order_id)">详情</el-button>
            <el-button type="text" @click="handleEdit(scope.row.order_id)">编辑</el-button>
            <el-button type="text" @click="handlePrint(scope.row.order_id)">打印</el-button>
            <el-button type="text" @click="handleDelete(scope.row.order_id)" style="color: #f56c6c;">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <button @click="prevPage" :disabled="currentPage <= 1" class="page-button">上一页</button>
        <span class="page-info">当前第 {{ currentPage }} 页 / 共 {{ totalPages }} 页 - 共 {{ totalCount }} 条数据</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="page-button">下一页</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import ordersAPI from '@/api/orders'
import customersAPI from '@/api/customers'

export default {
  name: 'OrdersListView',
  data() {
    return {
      orders: [],
      searchKeyword: '',
      loading: false,
      batchDeleting: false,
      batchPrinting: false, // 添加批量打印加载状态
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      selectedOrderIds: [],
      filterForm: {
        customer_id: null,
        status: '',
        start_date: '',
        end_date: ''
      },
      customers: [],
      useFilter: false // 标记是否使用筛选
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize) || 1
    },
    paginatedData() {
      return this.orders
    }
  },
  created() {
    this.fetchCustomers() // 添加这行来获取客户列表
    this.fetchOrdersData()
  },
  methods: {
    // 获取客户列表
    async fetchCustomers() {
      try {
        const response = await customersAPI.inquireCustomers({ per_page: 1000 })
        this.customers = response.customers || []
      } catch (error) {
        console.error('获取客户列表失败:', error)
        ElMessage.error('获取客户列表失败')
      }
    },

    // 获取订单列表
    async fetchOrdersData() {
      this.loading = true
      try {
        let response
        if (this.useFilter) {
          const params = {
            ...this.filterForm,
            page: this.currentPage,
            per_page: this.pageSize
          }
          // 移除空值参数
          Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null) {
              delete params[key]
            }
          })
          response = await ordersAPI.filterOrders(params)
        } else {
          const params = {
            keyword: this.searchKeyword,
            page: this.currentPage,
            per_page: this.pageSize
          }
          response = await ordersAPI.inquireOrders(params)
        }

        this.orders = response.orders || []
        this.totalCount = response.total || 0
        this.currentPage = response.page || 1
      } catch (error) {
        console.error('获取订单列表失败:', error)
        ElMessage.error('获取订单列表失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.useFilter = false // 使用搜索而不是筛选
      this.currentPage = 1
      this.fetchOrdersData()
    },

    // 筛选
    handleFilter() {
      this.useFilter = true
      this.currentPage = 1
      this.fetchOrdersData()
    },

    resetFilter() {
      this.filterForm = {
        customer_id: null,
        status: '',
        start_date: '',
        end_date: ''
      }
      this.useFilter = false
      this.currentPage = 1
      this.fetchOrdersData()
    },

    // 分页
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchOrdersData()
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchOrdersData()
      }
    },

    // 查看详情
    handleViewDetail(orderId) {
      this.$router.push(`/orders/detail/${orderId}`)
    },

    // 编辑订单
    handleEdit(orderId) {
      this.$router.push({
        path: '/orders/create',
        query: { edit: true, order_id: orderId }
      })
    },

    // 打印订单
    handlePrint(orderId) {
      // 打开新窗口到订单详情页面进行打印
      const route = this.$router.resolve(`/orders/detail/${orderId}`)
      window.open(route.href, '_blank')
    },

    // 批量打印订单
    async handleBatchPrint() {
      if (this.selectedOrderIds.length === 0) {
        ElMessage.warning('请先选择要打印的订单')
        return
      }

      try {
        this.batchPrinting = true
        
        // 获取选中的订单数据
        const selectedOrders = this.orders.filter(order => 
          this.selectedOrderIds.includes(order.order_id)
        )

        // 批量打开打印页面
        selectedOrders.forEach((order, index) => {
          setTimeout(() => {
            const route = this.$router.resolve(`/orders/detail/${order.order_id}`)
            // 直接使用window.open，不需要赋值给变量
            window.open(route.href, `print_${order.order_id}`)
          }, index * 500) // 间隔500ms打开，避免被浏览器阻止
        })

        ElMessage.success(`已打开 ${selectedOrders.length} 个订单的打印页面`)
        
      } catch (error) {
        console.error('批量打印失败:', error)
        ElMessage.error('批量打印失败')
      } finally {
        this.batchPrinting = false
      }
    },

    // 删除订单
    async handleDelete(orderId) {
      try {
        await ElMessageBox.confirm('确定要删除该订单吗？删除后数据不可恢复！', '删除确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await ordersAPI.deleteOrder(orderId)
        ElMessage.success('订单删除成功')
        this.fetchOrdersData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除订单失败:', error)
          ElMessage.error('删除订单失败')
        }
      }
    },

    // 批量删除
    async handleBatchDelete() {
      if (this.selectedOrderIds.length === 0) {
        ElMessage.warning('请先选择要删除的订单')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${this.selectedOrderIds.length} 个订单吗？删除后数据不可恢复！`,
          '批量删除确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        this.batchDeleting = true

        // 逐个删除选中的订单
        const deletePromises = this.selectedOrderIds.map(orderId =>
          ordersAPI.deleteOrder(orderId)
        )
        await Promise.all(deletePromises)

        ElMessage.success(`成功删除 ${this.selectedOrderIds.length} 个订单`)
        this.selectedOrderIds = []
        this.fetchOrdersData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除订单失败:', error)
          ElMessage.error('批量删除订单失败')
        }
      } finally {
        this.batchDeleting = false
      }
    },

    // 表格选择变化
    handleSelectionChange(selectedRows) {
      this.selectedOrderIds = selectedRows.map(row => row.order_id)
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-')
    },

    // 获取状态标签类型
    getStatusType(status) {
      const statusMap = {
        '正常': 'primary',
        '已完成': 'success',
        '已取消': 'danger'
      }
      return statusMap[status] || 'info'
    }
  }
}
</script>

<style scoped>
.container01_search {
  width: 80%;
  margin-right: 10px;
  margin-top: 20px;
}

.container02 {
  width: 90%;
  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;
}

.page-button {
  margin-left: 10px;
  margin-right: 10px;
  padding: 8px 16px;
  border: 1px solid #dcdfe6;
  background-color: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.page-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.page-info {
  margin: 0 20px;
}

.container01_button {
  margin-bottom: 20px;
}

.search-col {
  display: flex;
  align-items: center;
}

/* 可以添加一些额外的样式来美化批量打印按钮 */
.el-button--success {
  background-color: #67c23a;
  border-color: #67c23a;
}

.el-button--success:hover {
  background-color: #5daf34;
  border-color: #5daf34;
}
</style>